import React, { useState, useEffect } from "react";
import {
  useHubspotStatus,
  useHubspotLinkOrg,
  useHubspotInstallUrl,
} from "../../hooks/useHubspot";
import hubspotLogo from "../../../../assets/hubspot-logo.png";
import "./integrations-page.scss";
import Card from "../../../../shared/components/dashboard-layout/components/card/cardComponent";

const IntegrationsPage = () => {
  const [showHubspotModal, setShowHubspotModal] = useState(false);

  const { status, loading: statusLoading, refetch: refetchStatus } = useHubspotStatus();

  const getHubspotStatusText = () => {
    if (statusLoading) return "Loading...";
    if (!status) return "Not connected";
    if (status.status === "pending") return "Pending";
    if (status.status === "installed") return "Connected";
    if (status.status === "error") return "Error";
    return "Not connected";
  };

  const getHubspotStatusClass = () => {
    if (!status) return "not-connected";
    if (status.status === "pending") return "pending";
    if (status.status === "installed") return "connected";
    if (status.status === "error") return "error";
    return "not-connected";
  };

  const handleConfigureClick = () => {
    setShowHubspotModal(true);
  };

  const handleModalClose = () => {
    setShowHubspotModal(false);
    refetchStatus();
  };

  return (
    <div className="integrations-container">
      <div className="pageHeaderTitle">
        <div className="pageTitle">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M8 12.2H15M8 16.2H12.38M10 3.67676V2.01221M14 3.67676V2.01221M7.2 7.70003H16.8C17.9201 7.70003 18.4802 7.70003 18.908 7.91639C19.2843 8.10807 19.5903 8.41421 19.782 8.79128C20 9.21692 20 9.77919 20 10.9037V16.8C20 17.9246 20 18.4868 19.782 18.9124C19.5903 19.2895 19.2843 19.5956 18.908 19.7873C18.4802 20.0037 17.9201 20.0037 16.8 20.0037H7.2C6.0799 20.0037 5.51984 20.0037 5.09202 19.7873C4.71569 19.5956 4.40973 19.2895 4.21799 18.9124C4 18.4868 4 17.9246 4 16.8V10.9037C4 9.77919 4 9.21692 4.21799 8.79128C4.40973 8.41421 4.71569 8.10807 5.09202 7.91639C5.51984 7.70003 6.07989 7.70003 7.2 7.70003Z" stroke="var(--text-primary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
          Integrations
        </div>
      </div>
      <p>
        Connect your favorite tools and services to enhance your workflow.
      </p>

      <div className="integrations-grid">
        <Card
          positiveButtonLabel={"Configure"}
          positiveButtonAction={handleConfigureClick}
        >
          <div className="integration-header">
            <div className="integration-logo hubspot-logo">
              <img src={hubspotLogo} alt="HubSpot" />
            </div>
            <div className="integration-info">
              <h3>HubSpot</h3>
              <p>CRM and marketing automation platform</p>
            </div>
          </div>

          <div className="integration-status my-3">
            <span className={`status-pill ${getHubspotStatusClass()}`}>
              {getHubspotStatusText()}
            </span>
          </div>
        </Card>

        {/* HubSpot Integration Card */}
        {/* <div className="integration-card">
          <div className="integration-header">
            <div className="integration-logo hubspot-logo">
              <img src={hubspotLogo} alt="HubSpot" />
            </div>
            <div className="integration-info">
              <h3>HubSpot</h3>
              <p>CRM and marketing automation platform</p>
            </div>
          </div>
          <div className="integration-status">
            <span className={`status-pill ${getHubspotStatusClass()}`}>
              {getHubspotStatusText()}
            </span>
          </div>
          <div className="integration-actions">
            <button className="bouncy-button small" onClick={handleConfigureClick}>
              <span>Configure</span>
            </button>
          </div>
        </div> */}
      </div>

      {/* HubSpot Integration Modal */}
      {showHubspotModal && (
        <HubspotIntegrationModal
          onClose={handleModalClose}
          initialStatus={status}
        />
      )}
    </div>
  );
};

const HubspotIntegrationModal = ({ onClose, initialStatus }) => {
  // Determine initial step based on status
  const getInitialStep = () => {
    if (initialStatus && initialStatus.hubspotHubId) {
      return 2; // Has hub ID, go to install step
    }
    return 1; // No integration, start from beginning
  };

  const [step, setStep] = useState(getInitialStep());
  const [hubId, setHubId] = useState(initialStatus?.hubspotHubId || "");
  const [localStatus, setLocalStatus] = useState(initialStatus);
  const [error, setError] = useState(null);

  const { linkOrg, loading: linkLoading, error: linkError } = useHubspotLinkOrg();
  const {
    installUrl,
    loading: installUrlLoading,
    error: installUrlError,
    fetchInstallUrl,
  } = useHubspotInstallUrl();
  const { status, loading: statusLoading, refetch: refetchStatus } = useHubspotStatus();

  // Fetch install URL when entering step 2
  useEffect(() => {
    if (step === 2) {
      fetchInstallUrl();
      refetchStatus();
    }
  }, [step, fetchInstallUrl, refetchStatus]);

  // Update local status when status changes
  useEffect(() => {
    if (status) {
      setLocalStatus(status);
    }
  }, [status]);

  const handleSaveAndContinue = async () => {
    if (!hubId.trim()) {
      setError("Please enter a valid Hub ID");
      return;
    }

    setError(null);
    try {
      const result = await linkOrg(hubId.trim());
      setLocalStatus(result);
      setStep(2);
    } catch (err) {
      setError(err.message || "Failed to link organization");
    }
  };

  const handleInstallClick = () => {
    if (installUrl) {
      window.open(installUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleRefreshStatus = async () => {
    await refetchStatus();
  };

  const getStatusText = () => {
    if (!localStatus) return "Pending installation";
    if (localStatus.status === "pending") return "Pending installation";
    if (localStatus.status === "installed") return "Connected";
    if (localStatus.status === "error") return "Error";
    return "Pending installation";
  };

  const getStatusClass = () => {
    if (!localStatus) return "pending";
    if (localStatus.status === "pending") return "pending";
    if (localStatus.status === "installed") return "connected";
    if (localStatus.status === "error") return "error";
    return "pending";
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content hubspot-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>HubSpot Integration</h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {/* Step Indicator */}
          <div className="step-indicator w-100">
            <div className={`step ${step === 1 ? "active" : ""} ${step > 1 ? "completed" : ""}`}>
              <span className="step-number">1</span>
              <span className="step-label">Configure Hub ID</span>
            </div>
            <div className="step-divider"></div>
            <div className={`step ${step === 2 ? "active" : ""}`}>
              <span className="step-number">2</span>
              <span className="step-label">Install & Status</span>
            </div>
          </div>

          {/* Step 1: Configure Hub ID */}
          {step === 1 && (
            <div className="step-content w-100">
              <div className="form-group">
                <label htmlFor="hubId">HubSpot Hub ID</label>
                <input
                  type="text"
                  id="hubId"
                  value={hubId}
                  onChange={(e) => setHubId(e.target.value)}
                  placeholder="Enter your HubSpot Hub ID"
                />
                <small>
                  You can find this in the top-right corner of your HubSpot portal UI.
                </small>
              </div>

              {(error || linkError) && (
                <div className="error-message">{error || linkError}</div>
              )}

              <div className="form-actions justify-content-end">
                <button
                  className="bouncy-button small"
                  onClick={handleSaveAndContinue}
                  disabled={linkLoading || !hubId.trim()}
                >
                  <span>{linkLoading ? "Saving..." : "Save & Continue"}</span>
                </button>
                <button className="bouncy-button small white" onClick={onClose}>
                  <span>Cancel</span>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Install & Status */}
          {step === 2 && (
            <div className="step-content">
              <div className="info-section">
                <div className="info-row">
                  <label>Hub ID:</label>
                  <span className="info-value">{localStatus?.hubspotHubId || hubId}</span>
                </div>
                <div className="info-row">
                  <label>Status:</label>
                  <span className={`status-pill ${getStatusClass()}`}>
                    {getStatusText()}
                  </span>
                </div>
                {localStatus?.status === "error" && localStatus?.lastError && (
                  <div className="error-details">
                    <label>Error:</label>
                    <span className="error-text">{localStatus.lastError}</span>
                  </div>
                )}
              </div>

              {localStatus?.status === "installed" ? (
                <div className="success-message">
                  <span className="success-icon">&#10003;</span>
                  HubSpot integration is successfully connected!
                </div>
              ) : (
                <>
                  <div className="install-section">
                    <button
                      className="bouncy-button install-button"
                      onClick={handleInstallClick}
                      disabled={installUrlLoading || !installUrl}
                    >
                      {installUrlLoading ? "Loading..." : "Install HubSpot App"}
                    </button>
                    {installUrlError && (
                      <div className="error-message small">{installUrlError}</div>
                    )}
                  </div>

                  <div className="instructions">
                    <p>
                      After installing in HubSpot, return here and click "Refresh status".
                    </p>
                  </div>

                  <div className="refresh-section">
                    <button
                      className="bouncy-button small white"
                      onClick={handleRefreshStatus}
                      disabled={statusLoading}
                    >
                      {statusLoading ? "Refreshing..." : "Refresh Status"}
                    </button>
                  </div>
                </>
              )}

              <div className="form-actions">
                {localStatus?.status !== "installed" && (
                  <button className="bouncy-button small white" onClick={() => setStep(1)}>
                    Back
                  </button>
                )}
                <button className="bouncy-button small" onClick={onClose}>
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntegrationsPage;
